import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Switch, FormControlLabel, Button, Alert,
  CircularProgress, Accordion, AccordionSummary, AccordionDetails,
  Stack, Chip, Tooltip, Snackbar, Tabs, Tab
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import TimelineIcon from '@mui/icons-material/Timeline';
import DescriptionIcon from '@mui/icons-material/Description';
import api from '../../services/api';

export default function ProfileFieldVisibilitySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [grouped, setGrouped] = useState([]);
  const [changes, setChanges] = useState({});
  const [expanded, setExpanded] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [careerEventTypes, setCareerEventTypes] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [typeChanges, setTypeChanges] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [fieldRes, careerRes, docRes] = await Promise.all([
        api.get('/profile-settings/field-visibility'),
        api.get('/profile-settings/career-event-types'),
        api.get('/profile-settings/document-types')
      ]);
      setGrouped(fieldRes.grouped || []);
      setExpanded(fieldRes.grouped?.map(g => g.category) || []);
      setCareerEventTypes(careerRes || []);
      setDocumentTypes(docRes || []);
    } catch (err) {
      setError('Failed to load settings: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = (fieldKey, currentValue) => {
    setChanges(prev => ({ ...prev, [fieldKey]: !currentValue }));
  };

  const handleTypeToggle = (type, kind) => {
    const key = `${kind}-${type.id}`;
    const currentValue = typeChanges.hasOwnProperty(key) ? typeChanges[key] : type.visibleInMyProfile;
    setTypeChanges(prev => ({ ...prev, [key]: !currentValue }));
  };

  const getTypeValue = (type, kind) => {
    const key = `${kind}-${type.id}`;
    return typeChanges.hasOwnProperty(key) ? typeChanges[key] : type.visibleInMyProfile;
  };

  const getFieldValue = (field) => {
    return changes.hasOwnProperty(field.fieldKey) ? changes[field.fieldKey] : field.isVisibleToEmployee;
  };

  const hasChanges = Object.keys(changes).length > 0 || Object.keys(typeChanges).length > 0;

  const handleSave = async () => {
    if (!hasChanges) return;
    try {
      setSaving(true);
      setError('');

      // Save field visibility changes
      if (Object.keys(changes).length > 0) {
        const updates = Object.entries(changes).map(([fieldKey, isVisibleToEmployee]) => ({
          fieldKey, isVisibleToEmployee
        }));
        await api.put('/profile-settings/field-visibility', updates);
      }

      // Save career event type changes
      const careerChanges = Object.entries(typeChanges)
        .filter(([key]) => key.startsWith('career-'))
        .map(([key, visibleInMyProfile]) => ({ id: parseInt(key.split('-')[1]), visibleInMyProfile }));
      if (careerChanges.length > 0) {
        await api.put('/profile-settings/career-event-types', careerChanges);
      }

      // Save document type changes
      const docChanges = Object.entries(typeChanges)
        .filter(([key]) => key.startsWith('doc-'))
        .map(([key, visibleInMyProfile]) => ({ id: parseInt(key.split('-')[1]), visibleInMyProfile }));
      if (docChanges.length > 0) {
        await api.put('/profile-settings/document-types', docChanges);
      }

      setSuccess('Settings saved successfully');
      setChanges({});
      setTypeChanges({});
      load();
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAccordion = (category) => {
    setExpanded(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderTypeList = (types, kind, emptyMessage) => (
    <Stack spacing={1}>
      {types.length === 0 ? (
        <Alert severity="info">{emptyMessage}</Alert>
      ) : (
        types.map(type => {
          const isVisible = getTypeValue(type, kind);
          const key = `${kind}-${type.id}`;
          const isChanged = typeChanges.hasOwnProperty(key);
          return (
            <Paper key={type.id} variant="outlined" sx={{ p: 1.5, bgcolor: isChanged ? 'action.selected' : 'transparent' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  {isVisible ? <VisibilityIcon color="success" fontSize="small" /> : <VisibilityOffIcon color="disabled" fontSize="small" />}
                  {type.icon && <Box component="span" sx={{ color: type.color || 'text.primary' }}>{type.icon}</Box>}
                  <Typography variant="body1">{type.name}</Typography>
                  <Chip size="small" label={type.code} variant="outlined" />
                  {isChanged && <Chip size="small" label="Modified" color="warning" />}
                </Stack>
                <FormControlLabel
                  control={<Switch checked={isVisible} onChange={() => handleTypeToggle(type, kind)} color="success" />}
                  label={isVisible ? 'Visible' : 'Hidden'}
                />
              </Stack>
            </Paper>
          );
        })
      )}
    </Stack>
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>My Profile Visibility Settings</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure what employees can see in their My Profile page
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<PersonIcon />} label="Profile Fields" iconPosition="start" />
        <Tab icon={<TimelineIcon />} label="Career Events" iconPosition="start" />
        <Tab icon={<DescriptionIcon />} label="Documents" iconPosition="start" />
      </Tabs>

      {activeTab === 0 && (
        <>
          {grouped.map(group => (
            <Accordion
              key={group.category}
              expanded={expanded.includes(group.category)}
              onChange={() => handleAccordion(group.category)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{group.category}</Typography>
                  <Chip
                    size="small"
                    label={`${group.fields.filter(f => getFieldValue(f)).length}/${group.fields.length} visible`}
                    color={group.fields.every(f => getFieldValue(f)) ? 'success' : 'default'}
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {group.fields.map(field => {
                    const isVisible = getFieldValue(field);
                    const isChanged = changes.hasOwnProperty(field.fieldKey);
                    return (
                      <Paper key={field.fieldKey} variant="outlined" sx={{ p: 1.5, bgcolor: isChanged ? 'action.selected' : 'transparent' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={1} alignItems="center">
                            {isVisible ? <VisibilityIcon color="success" fontSize="small" /> : <VisibilityOffIcon color="disabled" fontSize="small" />}
                            <Typography variant="body1">{field.displayName}</Typography>
                            {field.description && (
                              <Tooltip title={field.description}><InfoIcon fontSize="small" color="action" /></Tooltip>
                            )}
                            {isChanged && <Chip size="small" label="Modified" color="warning" />}
                          </Stack>
                          <FormControlLabel
                            control={<Switch checked={isVisible} onChange={() => handleToggle(field.fieldKey, isVisible)} color="success" />}
                            label={isVisible ? 'Visible' : 'Hidden'}
                          />
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Career Event Types
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Control which career event types employees can see in their profile's Career Events tab
          </Typography>
          {renderTypeList(careerEventTypes, 'career', 'No career event types configured')}
        </Paper>
      )}

      {activeTab === 2 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Document Types
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Control which document types employees can see in their profile's Documents tab
          </Typography>
          {renderTypeList(documentTypes, 'doc', 'No document types configured')}
        </Paper>
      )}

      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} message={success} />
    </Box>
  );
}

